import { ArrowRight01Icon } from "@hugeicons/core-free-icons";
import { useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { AppLink } from "@/components/common/app-link";
import { AccountStatusDot, ProviderMark, Tag } from "@/components/common/badges";
import { Icon } from "@/components/common/icon";
import { Page, PageTitle, Panel } from "@/components/common/page";
import { Stat } from "@/components/common/stat";
import { EmptyState, PageSkeleton } from "@/components/common/states";
import { AttentionList } from "@/components/overview/attention-list";
import { TrafficChart } from "@/components/overview/traffic-chart";
import { Button } from "@/components/ui/button";
import { q } from "@/data/queries";
import {
  compactNumber,
  formatLatency,
  formatNumber,
  formatPercent,
  relativeTime,
} from "@/lib/format";
import { projectPath } from "@/lib/nav";

const RANGES = ["24h", "7d", "30d", "90d"] as const;

export const Route = createFileRoute("/p/$project/")({
  loader: async ({ context }) => {
    await Promise.all([
      context.queryClient.ensureQueryData(q.metrics()),
      context.queryClient.ensureQueryData(q.accounts({ limit: 100 })),
      context.queryClient.ensureQueryData(q.webhooks("production")),
      context.queryClient.ensureQueryData(q.deliveries({ status: "failed", limit: 5 })),
      context.queryClient.ensureQueryData(q.calendarEvents({})),
      ...RANGES.map((range) => context.queryClient.ensureQueryData(q.usage(range))),
    ]);
  },
  pendingComponent: PageSkeleton,
  component: OverviewPage,
});

function OverviewPage() {
  const { project } = Route.useParams();
  const { data: metrics } = useSuspenseQuery(q.metrics());
  const { data: attentionPage } = useSuspenseQuery(q.accountsNeedingAttention());
  const { data: accounts } = useSuspenseQuery(q.accounts({ limit: 100 }));
  const { data: webhooks } = useSuspenseQuery(q.webhooks("production"));
  const { data: failures } = useSuspenseQuery(
    q.deliveries({ status: "failed", limit: 5 }),
  );
  const { data: events } = useSuspenseQuery(q.calendarEvents({}));
  const series = {
    "24h": useSuspenseQuery(q.usage("24h")).data,
    "7d": useSuspenseQuery(q.usage("7d")).data,
    "30d": useSuspenseQuery(q.usage("30d")).data,
    "90d": useSuspenseQuery(q.usage("90d")).data,
  };

  const failing = webhooks.filter((webhook) => webhook.health.failingSince);
  const upcoming = events.filter((event) => !event.allDay).slice(0, 4);

  return (
    <Page wide>
      <PageTitle
        title="Overview"
        description="Traffic, account health, and anything waiting on a person."
        actions={
          <Button size="sm" asChild>
            <AppLink to={projectPath(project, "/connect")}>Connect an account</AppLink>
          </Button>
        }
      />

      {/* The four numbers a customer checks first. Accounts needing attention
          is deliberately second, not buried: it is the only one they can act
          on, and the only one that costs them a customer if ignored. */}
      <div className="grid grid-cols-2 gap-px overflow-hidden rounded-lg border bg-border lg:grid-cols-4">
        <Stat
          label="Connected accounts"
          value={metrics.accountsConnected}
          footnote={`${accounts.total} total`}
        />
        <Stat
          label="Need attention"
          value={metrics.accountsNeedingAttention}
          tone={metrics.accountsNeedingAttention > 0 ? "warn" : undefined}
          footnote={
            metrics.accountsNeedingAttention > 0
              ? "A reconnect link fixes each one"
              : "Nothing waiting"
          }
        />
        <Stat
          label="Messages this period"
          value={compactNumber(metrics.messagesSent + metrics.messagesReceived)}
          footnote={`${formatNumber(metrics.messagesSent)} sent`}
        />
        <Stat
          label="API calls"
          value={compactNumber(metrics.apiCalls)}
          footnote={`${formatPercent(metrics.errorRate, 2)} errors · p95 ${formatLatency(metrics.latencyP95Ms)}`}
        />
      </div>

      <Panel
        title={`Needs attention (${attentionPage.length})`}
        description="Accounts the provider wants the end user to confirm"
        actions={
          <Button variant="ghost" size="sm" asChild>
            <AppLink to={projectPath(project, "/accounts?status=needs_attention")}>
              All accounts
              <Icon icon={ArrowRight01Icon} className="size-3.5" />
            </AppLink>
          </Button>
        }
        bodyClassName="p-0"
      >
        <AttentionList accounts={attentionPage} project={project} />
      </Panel>

      <div className="grid gap-4 xl:grid-cols-[minmax(0,1.6fr)_minmax(0,1fr)]">
        <Panel title="Traffic" bodyClassName="p-0">
          <TrafficChart series={series} />
        </Panel>

        <div className="flex flex-col gap-4">
          <Panel
            title="Webhook delivery"
            description="Production endpoints, last 24 hours"
            actions={
              <Button variant="ghost" size="sm" asChild>
                <AppLink to={projectPath(project, "/webhooks")}>Open</AppLink>
              </Button>
            }
            bodyClassName="divide-y"
          >
            {webhooks.map((webhook) => {
              const total = webhook.health.delivered + webhook.health.failed;
              const rate = total ? webhook.health.failed / total : 0;
              return (
                <div key={webhook.id} className="flex items-center gap-3 px-4 py-2.5">
                  <span
                    className={
                      webhook.health.failingSince
                        ? "size-2 shrink-0 rounded-full bg-stop"
                        : "size-2 shrink-0 rounded-full bg-ok"
                    }
                  >
                    <span className="sr-only">
                      {webhook.health.failingSince ? "Failing" : "Healthy"}
                    </span>
                  </span>
                  <span className="min-w-0 flex-1 truncate font-mono text-[12px]">
                    {webhook.url.replace("https://", "")}
                  </span>
                  <span className="shrink-0 text-[11.5px] text-muted-foreground tabular">
                    {rate > 0 ? formatPercent(rate, 1) : "0%"} failed
                  </span>
                </div>
              );
            })}
            {failing.length > 0 ? (
              <p className="px-4 py-2.5 text-[12px] leading-[1.5] text-stop">
                {failing[0]?.url.replace("https://", "")} has been failing for{" "}
                {relativeTime(failing[0]?.health.failingSince).replace(" ago", "")}.
                Deliveries are held and can be replayed once it recovers.
              </p>
            ) : null}
          </Panel>

          <Panel
            title="Next on the calendar"
            description="Across every connected calendar, this week"
            actions={
              <Button variant="ghost" size="sm" asChild>
                <AppLink to={projectPath(project, "/calendar")}>Open</AppLink>
              </Button>
            }
            bodyClassName="divide-y"
          >
            {upcoming.length === 0 ? (
              <EmptyState
                title="Nothing scheduled"
                description="No events in the window on any connected calendar."
                className="py-8"
              />
            ) : (
              upcoming.map((event) => (
                <AppLink
                  key={event.id}
                  to={projectPath(project, `/calendar/${event.id}`)}
                  className="flex items-center gap-3 px-4 py-2.5 transition-colors hover:bg-accent/50 focus-visible:ring-2 focus-visible:ring-ring/50 focus-visible:outline-none"
                >
                  <ProviderMark provider={event.provider} size="sm" />
                  <span className="min-w-0 flex-1 truncate text-[13px]">
                    {event.title}
                  </span>
                  {event.selfResponse === "needs_action" ? (
                    <Tag className="border-warn/30 bg-warn-soft/70 text-warn">
                      Unanswered
                    </Tag>
                  ) : null}
                  <span className="shrink-0 text-[11px] text-ink-faint tabular">
                    {relativeTime(event.start)}
                  </span>
                </AppLink>
              ))
            )}
          </Panel>

          <Panel
            title="Recent failures"
            description="Deliveries that exhausted their retries"
            bodyClassName="divide-y"
          >
            {failures.items.length === 0 ? (
              <EmptyState
                title="No failed deliveries"
                description="Every event in the window reached its endpoint."
                className="py-8"
              />
            ) : (
              failures.items.map((delivery) => (
                <AppLink
                  key={delivery.id}
                  to={projectPath(
                    project,
                    `/webhooks/${delivery.webhookId}?delivery=${delivery.id}`,
                  )}
                  className="flex items-center gap-2.5 px-4 py-2.5 transition-colors hover:bg-accent/50 focus-visible:ring-2 focus-visible:ring-ring/50 focus-visible:outline-none"
                >
                  <Tag mono className="border-stop/30 bg-stop-soft/70 text-stop">
                    {delivery.responseStatus ?? "no response"}
                  </Tag>
                  <span className="min-w-0 flex-1 truncate font-mono text-[11.5px] text-muted-foreground">
                    {delivery.event}
                  </span>
                  <span className="shrink-0 text-[11px] text-ink-faint">
                    {relativeTime(delivery.at)}
                  </span>
                </AppLink>
              ))
            )}
          </Panel>
        </div>
      </div>

      <Panel
        title="Accounts"
        description="Every connected account and its last successful sync"
        actions={
          <Button variant="ghost" size="sm" asChild>
            <AppLink to={projectPath(project, "/accounts")}>
              All accounts
              <Icon icon={ArrowRight01Icon} className="size-3.5" />
            </AppLink>
          </Button>
        }
        bodyClassName="divide-y"
      >
        {accounts.items.slice(0, 6).map((account) => (
          <AppLink
            key={account.id}
            to={projectPath(project, `/accounts/${account.id}`)}
            className="flex items-center gap-3 px-4 py-2.5 transition-colors hover:bg-accent/50 focus-visible:ring-2 focus-visible:ring-ring/50 focus-visible:outline-none"
          >
            <ProviderMark provider={account.provider} size="sm" />
            <span className="min-w-0 flex-1 truncate text-[13px]">
              {account.handle}
            </span>
            <AccountStatusDot
              status={account.status}
              className="hidden w-36 shrink-0 sm:inline-flex"
            />
            <span className="w-16 shrink-0 text-right text-[11px] text-ink-faint">
              {relativeTime(account.lastSyncAt)}
            </span>
          </AppLink>
        ))}
      </Panel>
    </Page>
  );
}
